<script setup lang="ts">
import type { PlayerPartyInvite, PlayerPartyMember } from '@/api/public'
import { computed } from 'vue'

type InviteSlot = {
  key: string
  member: PlayerPartyMember | null
}

const props = defineProps<{
  invite: PlayerPartyInvite
  actionPending: boolean
  acceptDisabled: boolean
  acceptDisabledReason: string
  acceptNotice: string
  error: string | null
}>()

defineEmits<{
  accept: [invite: PlayerPartyInvite]
  decline: [inviteID: string]
  ignorePlayer: [steamID: string]
  dismiss: [inviteID: string]
  clearError: []
}>()

const members = computed(() => props.invite.party.members)
const slots = computed<InviteSlot[]>(() => {
  const items: InviteSlot[] = members.value.map(member => ({
    key: member.steam_id,
    member,
  }))
  while (items.length < 5) {
    items.push({ key: `empty-${items.length}`, member: null })
  }
  return items.slice(0, 5)
})
const inviterName = computed(() => (
  props.invite.invited_by_persona_name || props.invite.invited_by_steam_id
))

function memberName(member: PlayerPartyMember): string {
  return member.persona_name || member.steam_id
}
</script>

<template>
  <div class="player-party-invite-layer">
    <div class="player-party-invite-backdrop" aria-hidden="true"></div>
    <section
      id="player-party-invite-dialog"
      class="player-party-invite-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="player-party-invite-title"
      aria-describedby="player-party-invite-description"
      tabindex="-1"
    >
      <header>
        <button
          class="player-party-invite-close"
          type="button"
          aria-label="Закрыть приглашение и вернуться к нему позже"
          title="Вернуться позже"
          @click="$emit('dismiss', invite.id)"
        >
          <img src="/shared/assets/x_close_png.png" alt="" aria-hidden="true" />
        </button>
        <span>Приглашение</span>
        <h2 id="player-party-invite-title">Вступить в группу?</h2>
        <p id="player-party-invite-description">
          <strong>{{ inviterName }}</strong> приглашает вас играть вместе.
        </p>
      </header>

      <ol class="player-party-invite-roster" aria-label="Состав приглашающей группы">
        <li
          v-for="slot in slots"
          :key="slot.key"
          :class="{ empty: !slot.member }"
        >
          <span class="player-party-invite-avatar">
            <img
              v-if="slot.member?.avatar_url"
              :src="slot.member.avatar_url"
              alt=""
            />
            <strong v-else-if="slot.member" aria-hidden="true">
              {{ memberName(slot.member).slice(0, 1).toUpperCase() }}
            </strong>
            <img
              v-else
              class="empty-icon"
              src="/shared/assets/friend_png.png"
              alt=""
              aria-hidden="true"
            />
            <img
              v-if="slot.member?.leader && members.length >= 2"
              class="leader-overlay"
              src="/shared/assets/party_leader_overlay_psd.png"
              alt=""
              aria-hidden="true"
            />
          </span>
          <span>{{ slot.member ? memberName(slot.member) : 'Свободно' }}</span>
        </li>
      </ol>

      <div v-if="error" class="player-party-invite-error" role="alert">
        <span>{{ error }}</span>
        <button type="button" aria-label="Скрыть ошибку" @click="$emit('clearError')">×</button>
      </div>

      <p
        v-if="acceptDisabled && acceptDisabledReason"
        class="player-party-invite-lock"
        role="status"
      >
        {{ acceptDisabledReason }}
      </p>
      <p
        v-else-if="acceptNotice"
        class="player-party-invite-lock replacement"
        role="status"
      >
        {{ acceptNotice }}
      </p>

      <footer>
        <button
          class="ignore"
          type="button"
          :disabled="actionPending"
          :aria-label="`Игнорировать ${inviterName}`"
          @click="$emit('ignorePlayer', invite.invited_by_steam_id)"
        >
          <img src="/shared/assets/x_close_png.png" alt="" aria-hidden="true" />
          <span>Игнорировать</span>
        </button>
        <button
          class="decline"
          type="button"
          :disabled="actionPending"
          @click="$emit('decline', invite.id)"
        >
          Отклонить
        </button>
        <button
          class="accept"
          type="button"
          :disabled="actionPending || acceptDisabled"
          @click="$emit('accept', invite)"
        >
          Принять
        </button>
      </footer>
    </section>
  </div>
</template>
